import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../../auth/AuthProvider.js';

const ALLOWED_MIME = ['image/png', 'image/jpeg'];
const MAX_BYTES = 2 * 1024 * 1024;

export function Logo() {
  const { activeOrgId } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('no_file');
      const fd = new FormData();
      fd.append('file', file);
      const base = import.meta.env.VITE_API_URL ?? '/api';
      const res = await fetch(`${base}/orgs/${activeOrgId}/logo`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const body: unknown = await res.json().catch(() => ({}));
        const b = body as { error?: string };
        throw new Error(b.error ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['config-status'] });
      nav('/app');
    },
    onError: (e: unknown) => {
      setError(e instanceof Error ? e.message : 'Error al subir');
    },
  });

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setError(null);
    if (!f) {
      setFile(null);
      setPreview(null);
      return;
    }
    if (!ALLOWED_MIME.includes(f.type)) {
      setError('Tipo de archivo no permitido (PNG o JPG).');
      return;
    }
    if (f.size > MAX_BYTES) {
      setError('El archivo supera 2 MB.');
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-medium">Logo</h2>
      <p className="text-sm text-slate-600">
        PNG o JPG, máximo 2 MB. Aparecerá en facturas y reportes.
      </p>
      <input type="file" accept="image/png,image/jpeg" onChange={onFile} />
      {preview && <img src={preview} alt="preview" className="max-h-32" />}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={() => nav('/app')} className="text-sm text-slate-600">
          Saltar →
        </button>
        <button
          type="button"
          disabled={!file || upload.isPending}
          onClick={() => upload.mutate()}
          className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
        >
          Subir y finalizar
        </button>
      </div>
    </div>
  );
}
